import { useEffect, useRef } from "react";
import { flushSync } from "react-dom";

const LEVELS = ["item", "group", "material"];
const ARROW_KEYS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]);

export default function usePickerKeyboardNavigation(
  pagination,
  enabled = true,
  initialItemKey = null,
  onTypeahead = null
) {
  const pendingPageDirection = useRef(null);
  const wasEnabled = useRef(false);

  useEffect(() => {
    const direction = pendingPageDirection.current;
    if (!direction) return;

    const items = Array.from(
      document.querySelectorAll('.modal.show [data-picker-nav="item"]')
    );
    const regularItems = items.filter(
      (item) => !item.hasAttribute("data-picker-nav-empty")
    );
    const focusItems = regularItems.length ? regularItems : items;
    const target =
      direction > 0 ? focusItems[0] : focusItems[focusItems.length - 1];
    target?.focus();
    pendingPageDirection.current = null;
  }, [pagination.page]);

  useEffect(() => {
    if (!enabled) {
      wasEnabled.current = false;
      return undefined;
    }
    if (wasEnabled.current) return undefined;
    wasEnabled.current = true;

    const modal =
      Array.from(document.querySelectorAll(".modal.show")).at(-1) ||
      document.querySelector('.modal[role="dialog"]');
    const items = Array.from(
      modal?.querySelectorAll(
        '[data-picker-nav-group="item"] [data-picker-nav="item"]'
      ) || []
    );
    const selectedItem =
      initialItemKey == null
        ? null
        : items.find(
            (item) => item.dataset.pickerKey === String(initialItemKey)
          );
    const regularItem = items.find(
      (item) => !item.hasAttribute("data-picker-nav-empty")
    );
    (selectedItem || regularItem || items[0])?.focus();
    return undefined;
  }, [enabled, initialItemKey]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      const modals = Array.from(document.querySelectorAll(".modal.show"));
      const modal = modals[modals.length - 1];
      const focusedItem = event.target.closest?.('[data-picker-nav="item"]');
      if (focusedItem && modal?.contains(focusedItem)) {
        event.preventDefault();
        // Commit the focused card before the confirmation reads its selection.
        flushSync(() => focusedItem.click());
      }
      const confirmButton = modal?.querySelector("[data-picker-confirm]");
      if (confirmButton && !confirmButton.disabled) {
        event.preventDefault();
        confirmButton.click();
      }
      return;
    }
    const target = event.target;
    const isTextEntry =
      target instanceof HTMLElement &&
      (target.matches("input, textarea, select") || target.isContentEditable);
    if (
      onTypeahead &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.altKey &&
      !event.metaKey &&
      !isTextEntry
    ) {
      event.preventDefault();
      onTypeahead(event.key);
      return;
    }
    if (!ARROW_KEYS.has(event.key)) return;

    if (
      event.ctrlKey &&
      (event.key === "ArrowUp" || event.key === "ArrowDown")
    ) {
      const currentLevel =
        event.target.closest?.("[data-picker-nav]")?.dataset.pickerNav;
      const levels = LEVELS.filter((level) =>
        document.querySelector(
          `.modal.show [data-picker-nav-group="${level}"] [data-picker-nav]`
        )
      );
      if (!levels.length) return;

      event.preventDefault();
      const currentIndex = levels.indexOf(currentLevel);
      const step = event.key === "ArrowUp" ? 1 : -1;
      const nextIndex =
        currentIndex < 0
          ? step > 0
            ? 0
            : levels.length - 1
          : (currentIndex + step + levels.length) % levels.length;
      const target = document.querySelector(
        `.modal.show [data-picker-nav-group="${levels[nextIndex]}"] [data-picker-nav]`
      );
      if (levels[nextIndex] === "item") {
        const regularItem = document.querySelector(
          '.modal.show [data-picker-nav-group="item"] [data-picker-nav="item"]:not([data-picker-nav-empty])'
        );
        (regularItem || target)?.focus();
      } else {
        target?.focus();
      }
      return;
    }

    if (
      event.ctrlKey &&
      (event.key === "ArrowLeft" || event.key === "ArrowRight")
    ) {
      event.preventDefault();
      if (pagination.pageCount <= 1) return;

      const direction = event.key === "ArrowRight" ? 1 : -1;
      const nextPage = Math.max(
        1,
        Math.min(pagination.pageCount, pagination.page + direction)
      );
      if (nextPage !== pagination.page) {
        pendingPageDirection.current = direction;
        pagination.setPage(nextPage);
      }
      return;
    }

    if (event.ctrlKey) return;

    const current = event.target.closest?.("[data-picker-nav]");
    const group = current?.closest("[data-picker-nav-group]");
    if (!current || !group) {
      const regularItem = document.querySelector(
        '.modal.show [data-picker-nav-group="item"] [data-picker-nav="item"]:not([data-picker-nav-empty])'
      );
      const emptyItem = document.querySelector(
        '.modal.show [data-picker-nav-group="item"] [data-picker-nav-empty]'
      );
      const target = regularItem || emptyItem;
      if (target) {
        event.preventDefault();
        target.focus();
      }
      return;
    }

    const siblings = Array.from(
      group.querySelectorAll("[data-picker-nav]")
    ).filter((element) => !element.hasAttribute("disabled"));
    const currentIndex = siblings.indexOf(current);
    if (currentIndex < 0 || siblings.length < 2) return;

    const horizontal = event.key === "ArrowLeft" || event.key === "ArrowRight";
    const direction =
      event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 1;
    const currentRect = current.getBoundingClientRect();
    const hasLayout = siblings.some((element) => {
      const rect = element.getBoundingClientRect();
      return rect.width > 0 || rect.height > 0;
    });
    event.preventDefault();

    if (!hasLayout) {
      siblings[currentIndex + direction]?.focus();
      return;
    }

    const currentX = currentRect.left + currentRect.width / 2;
    const currentY = currentRect.top + currentRect.height / 2;
    const crossLimit = horizontal
      ? Math.max(currentRect.height / 2, 6)
      : Math.max(currentRect.width / 2, 6);
    const candidates = siblings
      .filter((element) => element !== current)
      .map((element) => {
        const rect = element.getBoundingClientRect();
        const dx = rect.left + rect.width / 2 - currentX;
        const dy = rect.top + rect.height / 2 - currentY;
        const primary = horizontal ? dx : dy;
        const cross = horizontal ? dy : dx;
        return { element, primary, cross };
      })
      .filter(
        ({ primary, cross }) =>
          primary * direction > 0 && Math.abs(cross) <= crossLimit
      )
      .sort((a, b) => Math.abs(a.primary) - Math.abs(b.primary));

    candidates[0]?.element.focus();
  };

  const handlerRef = useRef(handleKeyDown);
  handlerRef.current = handleKeyDown;

  useEffect(() => {
    if (!enabled) return undefined;
    const onDocumentKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (!document.querySelector(".modal.show")) return;
      handlerRef.current(event);
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, [enabled]);

  return handleKeyDown;
}
